/**
 * PartnerDashboard — кабинет партнёра. Шаг Flow (step="partner").
 *
 * Показывает: модель партнёра, баланс (доступно / в холде / выплачено), оборот и число сделок,
 * реферальную ссылку с QR и промокодом, а для модели «Агент» — базовый курс TTEX, поле наценки
 * и авторасчёт итогового курса клиента.
 *
 * Пропсы: me — состояние партнёра с бэка; onBack; onReload — перечитать состояние;
 * onReferrals / onFinance — переходы в подэкраны.
 * Персональных данных клиентов здесь нет и быть не может: бэкенд отдаёт только агрегаты.
 */
import { useCallback, useEffect, useState } from "react";
import QRCode from "qrcode";
import { Screen, Card, Row } from "@/components/Screen";
import { BackLink } from "@/components/BackLink";
import { Button } from "@/components/Button";
import { Note } from "@/components/Note";
import { ErrorText } from "@/components/ErrorText";
import { Field } from "@/components/Field";
import { copyToClipboard } from "@/lib/clipboard";
import { friendlyError, partnerApi, type PartnerMe } from "@/lib/api";

// Ссылка в мессенджер приземляется как «/start CODE» — обработчик есть и в Telegram, и в WWChat.
const BOT_LINK = "https://t.me/ttex_robot";
const WEB_LINK = "https://ttex.online";

type Props = {
  me: Extract<PartnerMe, { is_partner: true }>;
  onBack: () => void;
  onReload: () => void;
  onReferrals: () => void;
  onFinance: () => void;
};

export function PartnerDashboard({ me, onBack, onReload, onReferrals, onFinance }: Props) {
  const code = me.code ?? "";
  const tgLink = code ? `${BOT_LINK}?start=${code}` : "";
  const webLink = code ? `${WEB_LINK}/?ref=${code}` : "";

  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!tgLink) return;
    QRCode.toDataURL(tgLink, { width: 260, margin: 1, color: { dark: "#0C1013", light: "#ffffff" } })
      .then(setQr)
      .catch(() => setQr(null));
  }, [tgLink]);

  const copy = useCallback(async (text: string, what: string) => {
    if (await copyToClipboard(text)) {
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    }
  }, []);

  if (me.status === "pending") {
    return (
      <Screen title="Заявка на рассмотрении">
        <BackLink onClick={onBack} className="mb-4">← Назад</BackLink>
        <Note>
          Мы проверяем заявку вручную — так программа защищена от случайных участников.
          Как только её одобрят, здесь появятся ссылка, код и статистика.
        </Note>
      </Screen>
    );
  }

  if (me.status === "blocked") {
    return (
      <Screen title="Партнёрство приостановлено">
        <BackLink onClick={onBack} className="mb-4">← Назад</BackLink>
        <Note variant="danger">
          {me.reject_reason || "Аккаунт заблокирован. Выплаты недоступны."} По вопросам напишите
          в поддержку.
        </Note>
      </Screen>
    );
  }

  return (
    <Screen title="Партнёрам" subtitle={me.model === "agent" ? "Модель «Агент»" : "Модель «Реферал»"}>
      <BackLink onClick={onBack} className="mb-4">← Назад</BackLink>

      <Card>
        <div className="text-[13px] text-white/45">Доступно к выводу</div>
        <div className="mt-1 text-[32px] font-semibold leading-none">
          {me.balance.available_usd.toFixed(2)} <span className="text-[20px] text-white/50">$</span>
        </div>
        <div className="mt-3 border-t border-white/[0.06] pt-2">
          <Row label="В холде" value={`${me.balance.hold_usd.toFixed(2)} $`} />
          <Row label="Ожидает сделки" value={`${me.balance.pending_usd.toFixed(2)} $`} />
          <Row label="Выплачено" value={`${me.balance.paid_usd.toFixed(2)} $`} />
        </div>
        <Button className="mt-3" onClick={onFinance}>
          Финансы и выплаты
        </Button>
      </Card>

      {me.balance.available_usd < 0 && (
        <Note variant="warning" className="mt-3">
          Баланс отрицательный: по одной из сделок был возврат уже после выплаты. Долг спишется
          с ближайших начислений.
        </Note>
      )}

      <Card className="mt-3">
        <Row label="Оборот клиентов" value={`${me.stats.turnover_rub.toLocaleString("ru-RU")} ₽`} />
        <Row label="Сделок" value={me.stats.deals} />
        <Row label="Клиентов" value={me.stats.clients} />
        <Row label="Переходов по ссылке" value={me.clicks} />
        <Button variant="ghost" className="mt-3" onClick={onReferrals}>
          Мои рефералы
        </Button>
      </Card>

      {me.model === "agent" && <AgentMarkup me={me} onSaved={onReload} />}

      <Card className="mt-3">
        <div className="text-[13px] text-white/45">Ваш код</div>
        <div className="mt-1 font-mono text-[24px] font-semibold tracking-wider">{code.toUpperCase()}</div>
        <p className="mt-1 text-[12px] leading-snug text-white/40">
          Клиент может ввести его вручную в боте — или просто перейти по вашей ссылке.
        </p>

        {qr && (
          <div className="mt-4 flex justify-center">
            <img src={qr} alt="QR" width={200} height={200} className="h-[200px] w-[200px] rounded-lg" />
          </div>
        )}

        <div className="mt-4 space-y-2">
          <LinkRow label="Ссылка в Telegram" value={tgLink} onCopy={() => copy(tgLink, "tg")} copied={copied === "tg"} />
          <LinkRow label="Ссылка на сайт" value={webLink} onCopy={() => copy(webLink, "web")} copied={copied === "web"} />
          <LinkRow label="Код" value={code.toUpperCase()} onCopy={() => copy(code, "code")} copied={copied === "code"} />
        </div>
      </Card>

      <Card className="mt-3">
        <div className="text-[13px] font-medium text-white/70">Как это работает</div>
        <p className="mt-1.5 text-[13px] leading-relaxed text-white/45">
          {me.model === "agent"
            ? "Клиент по вашей ссылке покупает по курсу с вашей наценкой. Разница — ваш заработок."
            : `С каждой сделки приведённого клиента вам начисляется ${me.program.l1_pct}% от её оборота.`}{" "}
          Вознаграждение появляется после исполнения сделки, {me.program.hold_days} дней холд,
          затем становится доступным к выводу.
        </p>
      </Card>
    </Screen>
  );
}

function LinkRow({
  label,
  value,
  onCopy,
  copied,
}: {
  label: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
}) {
  return (
    <button type="button" onClick={onCopy} className="block w-full text-left">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5">
        <div className="text-[12px] text-white/40">{label}</div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <span className="truncate text-[13px] text-white/80">{value}</span>
          <span className={`shrink-0 text-[12px] ${copied ? "text-brand" : "text-white/40"}`}>
            {copied ? "Скопировано" : "Копировать"}
          </span>
        </div>
      </div>
    </button>
  );
}

/** Поле наценки агента с авторасчётом итогового курса. Потолок проверяет сервер — здесь
 *  только подсказка, чтобы не отправлять заведомо неверное значение. */
function AgentMarkup({
  me,
  onSaved,
}: {
  me: Extract<PartnerMe, { is_partner: true }>;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(String(me.markup_pct));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pct = Number(value.replace(",", "."));
  const valid = Number.isFinite(pct) && pct >= 0;
  const max = me.program.max_markup_pct;
  const overCap = max !== null && valid && pct > max;
  const preview = me.base_rate !== null && valid ? me.base_rate * (1 + pct / 100) : null;

  const save = async () => {
    if (!valid || overCap || busy) return;
    setBusy(true);
    setError("");
    try {
      await partnerApi.setMarkup(pct);
      onSaved();
    } catch (e) {
      setError(friendlyError(e, "Не удалось сохранить наценку."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-3">
      <Row
        label="Базовый курс TTEX"
        value={me.base_rate !== null ? `${me.base_rate.toFixed(2)} ₽` : "—"}
      />
      <Field
        label="Моя наценка, %"
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <Row
        label="Итоговый курс клиента"
        value={preview !== null ? `${preview.toFixed(2)} ₽` : "—"}
      />
      {overCap && (
        <ErrorText className="mt-1">Максимальная наценка сейчас — {max}%.</ErrorText>
      )}
      <ErrorText className="mt-1">{error}</ErrorText>
      <Button
        className="mt-3"
        variant="ghost"
        disabled={!valid || overCap || busy || pct === me.markup_pct}
        onClick={save}
      >
        {busy ? "Сохранение…" : "Сохранить наценку"}
      </Button>
    </Card>
  );
}
